import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IRights } from '@optima-stride-ai/core';
import { Router } from '@angular/router';
import { CollaboraIframeUrlResponse, CollaboraService } from '../../services/collabora.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { GlobalStateService } from '../../../service/global.service';
import { SocketService } from '../../services/socket.service';
import { SocketEvents, UploadFileResponse } from '../../../types/socket-events.types';
import { WopiApiProxyService } from '../../services/wopi-api-proxy.service';
import { UserContextService } from '@optima-stride-ai/optima-platform';
import { isXappCapEmbedMode } from '@optima-stride-ai/optima-app-framework';
import { DocumentMode } from '../../shared/enums/document-mode.enum';
import { ProcessProgressView, ProgressIndicatorComponent, ShimmerVariant } from '@optima-stride-ai/optima-components';
import { DocumentLoaderComponent } from '../../shared/components/document-loader/document-loader.component';
import { EntityLayoutConfigService } from '../../services/entity-layout-config.service';
import { Subscription } from 'rxjs';

interface DebugInfo {
    timestamp: string;
    message: string;
}

interface CollaboraActionSaveMessage {
    MessageId: 'Action_Save';
}

interface CollaboraHostReadyMessage {
    MessageId: 'Host_PostmessageReady';
}

interface CollaboraUnoSaveMessage {
    MessageId: 'Send_UNO_Command';
    Values: {
        Command: '.uno:Save';
    };
}

interface CollaboraActionCloseMessage {
    MessageId: 'Action_Close';
}

interface SaveTriggerOptions {
    showSavingUI: boolean;
    reason: string;
    requireDirty: boolean;
}


@Component({
    selector: 'app-collabora-viewer',
    standalone: true,
    imports: [CommonModule, ProgressIndicatorComponent],
    templateUrl: './collabora-viewer.component.html',
      styleUrls: ['./collabora-viewer.component.sass'],
})

export class CollaboraViewerComponent implements OnInit, OnDestroy {
     @Input() id?: string;

    @ViewChild('collaboraIframe') collaboraIframe?: ElementRef<HTMLIFrameElement>;
     
     
    // Input signal for rights - used to derive edit/view mode
    rights = input<IRights>();
    
    private router = inject(Router);
    private collaboraService = inject(CollaboraService);
    private sanitizer = inject(DomSanitizer);
    private socketService = inject(SocketService);
    private wopiApiProxyService = inject(WopiApiProxyService);
    private userContextService = inject(UserContextService);
    private entityLayoutConfigService = inject(EntityLayoutConfigService);
      readonly ProcessProgressView = ProcessProgressView;

    // API configuration
    private readonly API_BASE_URL = environment.apiUrl || 'https://docollab.optimastride.com';

    // State signals
    filename = signal<string>('');
    fileId = signal<string>(''); // Store fileId for cleanup
    loading = signal<boolean>(true);
    error = signal<string>('');
    showError = signal<boolean>(false);
    showDebug = signal<boolean>(false);
    iframeUrl = signal<SafeResourceUrl | null>(null);
    showIframe = signal<boolean>(false);
    isSaving = signal<boolean>(false);

    // Debug information
    debugInfo = signal<DebugInfo[]>([]);

    // Computed properties
    hasError = computed(() => this.showError());
    isLoading = computed(() => this.loading());
    iframeVisible = computed(() => this.showIframe());
    debugVisible = computed(() => this.showDebug());
    debugText = computed(() => {
        const info = this.debugInfo();
        return info.slice(-10).map((item: DebugInfo) => `${item.timestamp}: ${item.message}`).join('<br>');
    });
    
    /**
     * Derive Collabora mode from entity rights
     * - If rights.update is true -> 'edit' mode
     * - Otherwise -> 'view' mode (default)
     */
    collaboraMode = computed<DocumentMode>(() => {
        const entityRights = this.rights();
        // Default to 'view' if rights not present or update permission is false
        return entityRights?.update === true ? DocumentMode.Edit : DocumentMode.View;
    });

    // Expose enums for template
    readonly ShimmerVariant = ShimmerVariant;

    private keydownListener?: (event: KeyboardEvent) => void;
     // temporary implementation to listen for save requests
    private saveRequestSubscription?: Subscription;
    private saveTimeoutId?: NodeJS.Timeout;
    private autosaveIntervalId?: NodeJS.Timeout;
    private hasUnsavedChanges = false;
    private saveInFlight = false;
    private pendingSaveRequested = false;
    private readonly AUTO_SAVE_INTERVAL_MS = 500;
    private readonly SAVE_FALLBACK_TIMEOUT_MS = 10000;
    private readonly IFRAME_CLOSE_TIMEOUT_MS = 1000;
    private readonly WOPI_CLOSE_DRAIN_TIMEOUT_MS = 3000;
    private closePreparation?: Promise<void>;

    private readonly beforeUnloadListener = (): void => {
        // Best-effort final flush before browser refresh/close
        this.triggerSave({ showSavingUI: false, reason: 'beforeunload', requireDirty: true });
    };

    private readonly pageHideListener = (): void => {
        // Fires for tab close, navigation and mobile backgrounding
        this.triggerSave({ showSavingUI: false, reason: 'pagehide', requireDirty: true });
    };

    private readonly visibilityChangeListener = (): void => {
        if (document.visibilityState === 'hidden') {
            this.triggerSave({ showSavingUI: false, reason: 'visibility-hidden', requireDirty: true });
        }
    };

    private readonly onlineListener = (): void => {
        this.addDebug('🌐 Browser is online, flushing pending save');
        this.pendingSaveRequested = true;
        this.flushPendingSave();
    };

    private readonly socketConnectListener = (): void => {
        this.addDebug('🔌 Socket connected/reconnected, flushing pending save');
        this.pendingSaveRequested = true;
        this.flushPendingSave();
    };
    private readonly uploadFileResponseListener = (response: UploadFileResponse): void => {
        if (response.success) {
            if (this.saveTimeoutId) {
                clearTimeout(this.saveTimeoutId);
                this.saveTimeoutId = undefined;
            }
            this.saveInFlight = false;
            this.hasUnsavedChanges = false;
            this.isSaving.set(false);
            this.addDebug(`✅ File update API call completed successfully`);
            this.flushPendingSave();
        } else {
            if (this.saveTimeoutId) {
                clearTimeout(this.saveTimeoutId);
                this.saveTimeoutId = undefined;
            }
            this.saveInFlight = false;
            this.isSaving.set(false);
            this.addDebug(`❌ File update failed: ${response.error}`);
            this.pendingSaveRequested = true;
        }
    };
    private readonly windowMessageListener = (event: MessageEvent<unknown>): void => {
        const isFromCollaboraIframe = event.source === this.collaboraIframe?.nativeElement?.contentWindow;
        const parsedData = this.parseMessageData(event.data);
        this.addDebug(`Message received (${isFromCollaboraIframe ? 'Collabora iframe' : 'other source'}, origin=${event.origin}): ${this.stringifyMessageData(parsedData)}`);

        // Check if this is a UI_Save message from Collabora (user clicked the save button in the editor)
        if (isFromCollaboraIframe && parsedData && typeof parsedData === 'object' && 'MessageId' in parsedData) {
            const messageId = (parsedData as any).MessageId;
            const modifiedFlag = this.extractModifiedFlag(parsedData);

            if (typeof modifiedFlag === 'boolean' && this.hasUnsavedChanges !== modifiedFlag) {
                this.hasUnsavedChanges = modifiedFlag;
                this.addDebug(`📝 Collabora dirty-state changed: ${modifiedFlag ? 'modified' : 'clean'}`);
            }

            if (messageId === 'UI_Save') {
                // Only show shimmer if not already saving (prevents duplicate timers)
                if (!this.isSaving()) {
                    this.isSaving.set(true);
                    this.saveInFlight = true;
                    this.addDebug(`💾 Collabora save button clicked, shimmer displayed`);

                    // Set fallback timeout to hide shimmer after 10 seconds
                    // Clear any existing timeout first
                    if (this.saveTimeoutId) {
                        clearTimeout(this.saveTimeoutId);
                    }
                    
                    this.saveTimeoutId = setTimeout(() => {
                        if (this.isSaving()) {
                            this.isSaving.set(false);
                            this.addDebug('⚠️ Shimmer hidden after timeout (file update may still be in progress)');
                        }
                        this.saveInFlight = false;
                        this.pendingSaveRequested = true;
                        this.saveTimeoutId = undefined;
                    }, 10000); // 10 second fallback timeout
                }
            }
        }
    };

 constructor(private global: GlobalStateService) {}

    async ngOnInit(): Promise<void> {
          this.global.setIsPreviewOpen(true);
          this.setupKeyboardListeners();
                    this.setupLifecycleSaveListeners();
          // temporary implementation to listen for save requests
            window.addEventListener('message', this.windowMessageListener);
            this.addDebug('Global window message listener attached');

          this.saveRequestSubscription = this.global.saveRequested$.subscribe((payload) => {
              if (!payload) {
                  return;
              }

              const activeId = this.id;
              if (!payload.entityId || payload.entityId === activeId) {
                  this.addDebug(`Received save request from ${payload.source || 'unknown'}; triggering saveDocument()`);
                  this.saveDocument();
              }
          });

        this.socketService.on(SocketEvents.CONNECT, this.socketConnectListener);

        // Listen for file upload completion (both FAB menu and Collabora button saves)
        this.socketService.on<UploadFileResponse>(SocketEvents.UPLOAD_FILE_RESPONSE, this.uploadFileResponseListener);
        
        // Get bizEntityId from route
        const bizEntityId = this.id;
        if (bizEntityId) {
            this.filename.set(bizEntityId);
            this.fileId.set(bizEntityId); // Store bizEntityId for cleanup
            this.addDebug(`File ID: ${bizEntityId}`);
            
            // CRITICAL: Ensure socket is connected before joining room
            if (!this.socketService.isConnected()) {
                if (isXappCapEmbedMode()) {
                    this.addDebug('Embedded Collabora viewer connecting to WOPI on demand...');
                    try {
                        await this.socketService.connectAndWait();
                    } catch (error: unknown) {
                        const details = error instanceof Error ? ` ${error.message}` : '';
                        this.showErrorMessage(`Failed to connect to server.${details} Please refresh the page.`);
                        return;
                    }
                } else {
                    this.addDebug(`⚠️ Socket not connected, waiting...`);
                    // Preserve the regular app's existing startup behavior.
                    await new Promise(resolve => setTimeout(resolve, 1000));

                    if (!this.socketService.isConnected()) {
                        this.addDebug(`❌ Socket still not connected after wait`);
                        this.showErrorMessage('Failed to connect to server. Please refresh the page.');
                        return;
                    }
                }
            }
            
            // Join file-specific room BEFORE loading Collabora
            try {
                const userId = localStorage.getItem('userId') || 'anonymous';
                const tenantId = localStorage.getItem('tenantId') || undefined;
                
                this.addDebug(`Socket connected: ${this.socketService.isConnected()}`);
                this.addDebug(`Joining file room: ${bizEntityId} (user: ${userId})`);
                
                await this.socketService.joinFileRoom(bizEntityId, userId, tenantId);
                this.addDebug(`✅ Joined file room successfully`);
                
                // Initialize file-specific WOPI API proxy listeners
                this.wopiApiProxyService.initializeForFile(bizEntityId);
                this.addDebug(`✅ WOPI API proxy initialized for this file`);
                this.startAutoSave();
                
                // Now load Collabora
                this.loadCollabora();
            } catch (error: any) {
                this.addDebug(`❌ Failed to join file room: ${error.message}`);
                this.showErrorMessage(`Failed to initialize file viewer: ${error.message}`);
            }
        } else {
            this.addDebug('ERROR: No file ID in input');
            this.showErrorMessage('No file ID provided');
        }
    }



    async ngOnDestroy(): Promise<void> {
        this.global.setIsPreviewOpen(false);
        await this.prepareForClose();
        this.removeLifecycleSaveListeners();

        // Collabora can issue final WOPI calls after its iframe has unloaded.
        // Keep this browser's proxy handlers available until those calls drain.
        await new Promise<void>(resolve => setTimeout(resolve, this.WOPI_CLOSE_DRAIN_TIMEOUT_MS));

        // ── Step 3: tear down socket listeners and local state ────────────────
            window.removeEventListener('message', this.windowMessageListener);
            this.saveRequestSubscription?.unsubscribe();
            this.socketService.off(SocketEvents.UPLOAD_FILE_RESPONSE, this.uploadFileResponseListener);
            this.socketService.off(SocketEvents.CONNECT, this.socketConnectListener);
            
            // Clear any pending save timeout
            if (this.saveTimeoutId) {
                clearTimeout(this.saveTimeoutId);
                this.saveTimeoutId = undefined;
            }
        
        // CRITICAL: Clean up file-specific resources
        const fileId = this.fileId();
        const userId = localStorage.getItem('userId') || 'anonymous';
        
        if (fileId) {
            this.addDebug(`Cleaning up for file: ${fileId}`);
            
            try {
                // Remove WOPI API proxy listeners for this file
                this.wopiApiProxyService.cleanupForFile(fileId);
                this.addDebug(`✅ WOPI API proxy cleaned up`);
                
                // Leave file room
                await this.socketService.leaveFileRoom(fileId, userId);
                this.addDebug(`✅ Left file room`);
                
                // Clear entity layout cache to force fresh thumbnail fetch on dashboard
                this.entityLayoutConfigService.clearEntityCache(fileId);
            } catch (error: any) {
            }
        }
        
        // Remove keyboard listener
        if (this.keydownListener) {
            document.removeEventListener('keydown', this.keydownListener);
        }
    }
     // temporary imlimentation to listen for save requests

    saveDocument(): void {
        this.triggerSave({ showSavingUI: true, reason: 'manual', requireDirty: false });
    }

    async prepareForClose(): Promise<void> {
        if (this.closePreparation) {
            return this.closePreparation;
        }

        this.closePreparation = (async () => {
            this.stopAutoSave();
            const iframe = this.collaboraIframe?.nativeElement;
            const iframeWindow = iframe?.contentWindow;

            if (iframeWindow && this.collaboraMode() === DocumentMode.Edit && this.hasUnsavedChanges) {
                const targetOrigin = this.getIframeTargetOrigin();
                try {
                    iframeWindow.postMessage(JSON.stringify({ MessageId: 'Action_Save' } as CollaboraActionSaveMessage), targetOrigin);
                    iframeWindow.postMessage(JSON.stringify({
                        MessageId: 'Send_UNO_Command',
                        Values: { Command: '.uno:Save' },
                    } as CollaboraUnoSaveMessage), targetOrigin);
                    this.addDebug('💾 Final save dispatched before closing Collabora');
                    await new Promise<void>(resolve => setTimeout(resolve, 300));
                } catch {
                    // The iframe may already be unloading; continue closing its session.
                }
            }

            if (iframe) {
                await this.unloadIframe(iframe);
            }
            this.iframeUrl.set(null);
            this.showIframe.set(false);
            this.addDebug('🚪 Collabora iframe session closed');
        })();

        return this.closePreparation;
    }

    private unloadIframe(iframe: HTMLIFrameElement): Promise<void> {
        return new Promise((resolve) => {
            let completed = false;
            const complete = (): void => {
                if (completed) {
                    return;
                }
                completed = true;
                clearTimeout(timeoutId);
                iframe.removeEventListener('load', onLoad);
                resolve();
            };
            const onLoad = (): void => complete();
            const timeoutId = setTimeout(complete, this.IFRAME_CLOSE_TIMEOUT_MS);

            iframe.addEventListener('load', onLoad, { once: true });
            iframe.src = 'about:blank';
        });
    }

    private triggerSave(options: SaveTriggerOptions): void {
        const iframeWindow = this.collaboraIframe?.nativeElement?.contentWindow;
        const targetOrigin = this.getIframeTargetOrigin();

        if (!iframeWindow) {
            this.pendingSaveRequested = true;
            this.addDebug(`Save skipped (${options.reason}): iframe contentWindow unavailable, marked pending`);
            return;
        }

        const mode = this.collaboraMode();
        if (mode !== DocumentMode.Edit) {
            const msg = `Save skipped (${options.reason}) while mode='${mode}'.`;
            this.addDebug(msg);
            return;
        }

        if (options.requireDirty && !this.hasUnsavedChanges) {
            this.addDebug(`Save skipped (${options.reason}): no unsaved changes`);
            return;
        }


        if (!navigator.onLine) {
            this.pendingSaveRequested = true;
            this.addDebug(`Save deferred (${options.reason}): browser offline`);
            return;
        }

        if (!this.socketService.isConnected()) {
            this.pendingSaveRequested = true;
            this.addDebug(`Save deferred (${options.reason}): socket disconnected`);
            return;
        }

        if (this.saveInFlight) {
            this.pendingSaveRequested = true;
            this.addDebug(`Save queued (${options.reason}): save in progress`);
            return;
        }

        this.saveInFlight = true;
        // Show shimmer during save
        if (options.showSavingUI) {
            this.isSaving.set(true);
            this.addDebug('Shimmer overlay displayed, waiting for file update API call to complete...');
        }
        const saveMessage: CollaboraActionSaveMessage = {
            MessageId: 'Action_Save',
        };

        const unoSaveMessage: CollaboraUnoSaveMessage = {
            MessageId: 'Send_UNO_Command',
            Values: {
                Command: '.uno:Save',
            },
        };

        // Collabora postMessage API is typically handled from JSON-string payloads.
        const saveMessageJson = JSON.stringify(saveMessage);
        const unoSaveMessageJson = JSON.stringify(unoSaveMessage);

        iframeWindow.postMessage(saveMessageJson, targetOrigin);
        this.addDebug(`Action_Save sent (${options.reason}, targetOrigin=${targetOrigin})`);

        // Fallback UNO save trigger to maximize compatibility across CODE versions.
        iframeWindow.postMessage(unoSaveMessageJson, targetOrigin);
        this.addDebug(`UNO .uno:Save sent (${options.reason}, targetOrigin=${targetOrigin})`);

        // Set fallback timeout to release save lock in case no response arrives
        // Clear any existing timeout first
        if (this.saveTimeoutId) {
            clearTimeout(this.saveTimeoutId);
        }
        
        this.saveTimeoutId = setTimeout(() => {
            this.saveInFlight = false;
            if (this.isSaving()) {
                this.isSaving.set(false);
                this.addDebug('⚠️ Shimmer hidden after timeout (file update may still be in progress)');
            }
            this.pendingSaveRequested = true;
            this.saveTimeoutId = undefined;
        }, this.SAVE_FALLBACK_TIMEOUT_MS);
    }

    private startAutoSave(): void {
        if (this.autosaveIntervalId) {
            return;
        }

        this.addDebug(`⏱️ Auto-save started (${this.AUTO_SAVE_INTERVAL_MS}ms interval)`);
        this.autosaveIntervalId = setInterval(() => {
            this.triggerSave({ showSavingUI: false, reason: 'interval-1s', requireDirty: true });
        }, this.AUTO_SAVE_INTERVAL_MS);
    }

    private stopAutoSave(): void {
        if (this.autosaveIntervalId) {
            clearInterval(this.autosaveIntervalId);
            this.autosaveIntervalId = undefined;
            this.addDebug('⏹️ Auto-save stopped');
        }
    }

    private flushPendingSave(): void {
        if (!this.pendingSaveRequested) {
            return;
        }

        this.pendingSaveRequested = false;
        this.triggerSave({ showSavingUI: false, reason: 'flush-pending', requireDirty: true });
    }

    private extractModifiedFlag(message: unknown): boolean | null {
        if (!message || typeof message !== 'object') {
            return null;
        }

        const messageObject = message as Record<string, unknown>;
        const values = messageObject['Values'];

        if (values && typeof values === 'object') {
            const valuesObject = values as Record<string, unknown>;
            if (typeof valuesObject['Modified'] === 'boolean') {
                return valuesObject['Modified'];
            }
            if (typeof valuesObject['modified'] === 'boolean') {
                return valuesObject['modified'];
            }
        }

        if (typeof messageObject['Modified'] === 'boolean') {
            return messageObject['Modified'];
        }
        if (typeof messageObject['modified'] === 'boolean') {
            return messageObject['modified'];
        }

        return null;
    }

    private setupLifecycleSaveListeners(): void {
        window.addEventListener('beforeunload', this.beforeUnloadListener);
        window.addEventListener('pagehide', this.pageHideListener);
        window.addEventListener('online', this.onlineListener);
        document.addEventListener('visibilitychange', this.visibilityChangeListener);
    }

    private removeLifecycleSaveListeners(): void {
        window.removeEventListener('beforeunload', this.beforeUnloadListener);
        window.removeEventListener('pagehide', this.pageHideListener);
        window.removeEventListener('online', this.onlineListener);
        document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }

    /**
     * Setup keyboard event listeners for debug toggle and navigation
     */
    private setupKeyboardListeners(): void {
        this.keydownListener = (event: KeyboardEvent) => {
            if (event.key === 'd' || event.key === 'D') {
                this.toggleDebug();
            }
            if (event.key === 'Escape') {
                this.goBack();
            }
        };
        document.addEventListener('keydown', this.keydownListener);
    }

    /**
     * Add debug information
     */
    private addDebug(message: string): void {
        const timestamp = new Date().toLocaleTimeString();
        this.debugInfo.update((info: DebugInfo[]) => [...info, { timestamp, message }]);
    }
     // temporary implementation to listen for save requests

    private stringifyMessageData(data: unknown): string {
        if (typeof data === 'string') {
            return data;
        }

        try {
            return JSON.stringify(data);
        } catch {
            return String(data);
        }
    }

    private parseMessageData(data: unknown): unknown {
        if (typeof data !== 'string') {
            return data;
        }

        try {
            return JSON.parse(data);
        } catch {
            return data;
        }
    }

    private getIframeTargetOrigin(): string {
        const iframeSrc = this.collaboraIframe?.nativeElement?.src;
        if (!iframeSrc) {
            return '*';
        }

        try {
            return new URL(iframeSrc).origin;
        } catch {
            return '*';
        }
    }

    private notifyCollaboraHostReady(): void {
        const iframeWindow = this.collaboraIframe?.nativeElement?.contentWindow;
        if (!iframeWindow) {
            this.addDebug('Host_PostmessageReady skipped: iframe contentWindow unavailable');
            return;
        }

        const targetOrigin = this.getIframeTargetOrigin();
        const readyMessage: CollaboraHostReadyMessage = {
            MessageId: 'Host_PostmessageReady',
        };

        iframeWindow.postMessage(JSON.stringify(readyMessage), targetOrigin);
        this.addDebug(`Host_PostmessageReady sent (targetOrigin=${targetOrigin})`);
    }

    /**
     * Toggle debug information visibility
     */
    toggleDebug(): void {
        this.showDebug.update((show: boolean) => !show);
    }

    /**
     * Navigate back to libraries page
     */
    goBack(): void {
        this.router.navigate(['/libraries']);
    }



    /**
     * Show error message
     */
    private showErrorMessage(message: string): void {
        this.addDebug(`ERROR: ${message}`);
        this.loading.set(false);
        this.error.set(message);
        this.showError.set(true);
    }

    /**
     * Retry loading the document
     */
    retryLoad(): void {
        this.loading.set(true);
        this.showError.set(false);
        this.error.set('');
        this.loadCollabora();
    }

    /**
     * Load Collabora iframe URL
     */
    private async loadCollabora(): Promise<void> {
        const filename = this.filename();

        if (!filename) {
            this.showErrorMessage('No filename provided in URL');
            return;
        }

        try {
            this.addDebug('Starting Collabora load...');
            this.addDebug(`API Base: ${this.API_BASE_URL}`);

            // Use Angular service instead of direct fetch
            this.addDebug('Requesting iframe URL from service...');

            // Get userId and tenantId from localStorage
            const userId = localStorage.getItem('userId') || undefined;
            const tenantId = localStorage.getItem('tenantId') || undefined;
            
            this.addDebug(`userId from localStorage: ${userId || 'not found'}`);
            this.addDebug(`tenantId from localStorage: ${tenantId || 'not found'}`);

            // Derive mode from entity rights (update=true -> 'edit', otherwise 'view')
            const mode = this.collaboraMode();
            this.addDebug(`Collabora mode derived from rights: ${mode} (update permission: ${this.rights()?.update ?? 'undefined'})`);

           await this.collaboraService.getIframeUrl(filename, mode, userId, this.userContextService.userName, tenantId).subscribe({
                next: (data: CollaboraIframeUrlResponse) => {
                    this.addDebug(`Got iframe URL: ${data.url.substring(0, 100)}...`);
                    this.addDebug(`WOPI Src: ${data.wopiSrc}`);
                    this.addDebug(`Token: ${data.accessToken ? 'Present' : 'Missing'}`);

                    // Sanitize the URL for security
                    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(data.url);
                    this.iframeUrl.set(safeUrl);
                    
                    // Hide loading and show iframe once URL is set
                    this.loading.set(false);
                    this.showIframe.set(true);

                    this.addDebug('Setting iframe src...');
                    this.addDebug('Iframe should now be visible');

                },
                error: (error: any) => {
                    this.addDebug(`Service error: ${error.message || error}`);

                    let errorMessage = `Error: ${error.message || error}\n\n`;
                    errorMessage += 'Please ensure:\n';
                    errorMessage += '1. Collabora Docker is running\n';
                    errorMessage += '2. Server is running on port 3000\n';
                    errorMessage += '3. The file exists in Documents/ folder';

                    this.showErrorMessage(errorMessage);
                }
            });

        } catch (error: any) {
            this.showErrorMessage(
                `Error: ${error.message}\n\n` +
                'Please ensure:\n' +
                '1. Collabora Docker is running\n' +
                '2. Server is running on port 3000\n' +
                '3. The file exists in Documents/ folder'
            );
        }
    }

    /**
     * Handle iframe load event
     */
    onIframeLoad(): void {
        this.addDebug('✅ Iframe loaded successfully!');
        this.addDebug('Collabora editor is now visible');

        // Ensure loading is off and iframe is visible
        this.loading.set(false);
        this.showIframe.set(true);

        this.notifyCollaboraHostReady();

        // Check if iframe actually has content
        setTimeout(() => {
            try {
                // This might throw due to CORS, but that's okay
                const iframe = document.getElementById('collabora-frame') as HTMLIFrameElement;
                if (iframe?.contentWindow) {
                    this.addDebug('Iframe window accessible');
                }
            } catch (e: any) {
                this.addDebug('Iframe CORS (expected): ' + e.message);
            }
        }, 1000);
    }

    /**
     * Handle iframe error event
     */
    onIframeError(event: Event): void {
        this.addDebug('❌ Iframe error event fired');

        this.showErrorMessage(
            'Failed to load the document editor iframe.\n\n' +
            'Please check:\n' +
            '1. Collabora Docker is running\n' +
            '2. File exists in Documents/ folder\n' +
            '3. Server is running on port 3000\n\n' +
            'Press D key to see debug info.'
        );
    }



}